// formExportJson — the builder kebab's "Export JSON": a pure client-side
// serializer of the SAVED design (no export route exists; the caller keeps the
// action disabled while there are unsaved changes, so the file always names a
// state that exists server-side). The element contract mirrors the legacy
// app's createStructureForExportJson so the file stays portable and
// re-importable: database identity is stripped (`_id`/`elementGlobalId` never
// emitted, the global id survives only as `uniqueIdToReplace`), trigger
// references are rewritten onto `triggerFlowReferenceId`, and tenant-bound
// resources — dataSource/companyField bindings, image media, geofence,
// watermarks — are blanked because none of them resolve in another company.
// `properties` stays a JSON-stringified blob inside the file, matching the
// wire/storage format. Top-level shape (confirmed): {title, icon, description,
// submitButtonColor, elements}.

import type { FormDetail, FormElementTriggers } from '@/api/endpoints/formsApi';
import { formElementDefaultProperties, mediaProperties } from './formElementDefaults';

/** One exported element row — the saved row with portable identity. */
interface ExportedElement {
  elementType: string;
  position: number;
  pageNumber: number;
  sectionNumber: number;
  properties: string;
  /** The saved elementGlobalId, renamed — what an import's backend swaps for
   *  fresh ids while re-linking the references below. */
  uniqueIdToReplace: string;
  triggers?: FormElementTriggers;
  triggerFlowReferenceId?: string | null;
}

/** The exported file. */
export interface FormExportJson {
  title: string;
  icon: string;
  description: string;
  submitButtonColor: string;
  elements: ExportedElement[];
}

/** Blank whatever can't cross tenants, per element type (legacy-exact patches;
 *  companyField added — same rationale as dataSource; the image media lives in
 *  basicProperties in THIS model, not legacy's layoutProperties). Reset values
 *  are destructured from the canonical defaults so they can never drift — and
 *  ONLY the tenant-bound keys, never a whole-group spread (that would clobber
 *  unrelated saved settings like `required`). The defaults share nested refs
 *  (bindingList, watermarkUsedFieldsMap) — safe only because the result is
 *  stringified immediately; don't hold onto `props`. Throws on unparseable
 *  properties — the caller's catch owns the error surface. */
const sanitizeProperties = (elementType: string, raw: string): string => {
  const props = JSON.parse(raw) as Record<string, Record<string, unknown>>;
  if (!['page', 'section', 'image'].includes(elementType)) {
    const {
      enableWatermark,
      watermarkText,
      watermarkAlignment,
      watermarkTextCenter,
      watermarkUsedFieldsMap,
    } = mediaProperties;
    props.mediaProperties = {
      ...props.mediaProperties,
      enableWatermark,
      watermarkText,
      watermarkAlignment,
      watermarkTextCenter,
      watermarkUsedFieldsMap,
    };
  }
  if (elementType === 'dataSource') {
    const { dataSource, displayColumn, valueColumn, bindingList } =
      formElementDefaultProperties.dataSource.basicProperties;
    props.basicProperties = {
      ...props.basicProperties,
      dataSource,
      displayColumn,
      valueColumn,
      bindingList,
    };
  } else if (elementType === 'companyField') {
    const { companyFieldId, companyFieldDisplayColumns, bindingList } =
      formElementDefaultProperties.companyField.basicProperties;
    props.basicProperties = {
      ...props.basicProperties,
      companyFieldId,
      companyFieldDisplayColumns,
      bindingList,
    };
  } else if (elementType === 'image') {
    const { fieldImage, fieldImageFile } = formElementDefaultProperties.image.basicProperties;
    props.basicProperties = {
      ...props.basicProperties,
      fieldImage,
      fieldImageFile,
    };
  } else if (elementType === 'location') {
    const { geofence, geofenceRange, geofenceRestricted, geofenceEnableDirection } =
      formElementDefaultProperties.location.validationProperties;
    props.validationProperties = {
      ...props.validationProperties,
      geofence,
      geofenceRange,
      geofenceRestricted,
      geofenceEnableDirection,
    };
  }
  return JSON.stringify(props);
};

/** Rewrite a row's grouped triggers for portability: ask_a_question/user_page
 *  entries move their target's elementGlobalId onto triggerFlowReferenceId
 *  (and record it in `referenced`, so the target row can stamp itself); the
 *  bare require_* kinds pass through untouched. */
const rewriteTriggers = (
  triggers: FormElementTriggers,
  referenced: Set<string>,
): FormElementTriggers =>
  Object.fromEntries(
    Object.entries(triggers).map(([matcher, byValue]) => [
      matcher,
      Object.fromEntries(
        Object.entries(byValue).map(([value, entries]) => [
          value,
          entries.map((entry) => {
            if (entry.type !== 'ask_a_question' && entry.type !== 'user_page') return entry;
            if (entry.elementGlobalId) referenced.add(entry.elementGlobalId);
            return {
              ...entry,
              triggerFlowReferenceId: entry.elementGlobalId ?? null,
              elementGlobalId: null,
            };
          }),
        ]),
      ),
    ]),
  );

/** Serialize a loaded form detail into the export file (pretty-printed).
 *  Two passes, like the legacy transform: triggers are rewritten first so the
 *  full referenced-id set exists before any row stamps its own
 *  triggerFlowReferenceId. */
export const buildFormExportJson = (detail: FormDetail): string => {
  const referenced = new Set<string>();
  const sorted = [...detail.design.elements].sort((a, b) => a.position - b.position);

  const staged = sorted.map((el) => ({
    el,
    triggers: el.triggers ? rewriteTriggers(el.triggers, referenced) : undefined,
    properties: sanitizeProperties(el.elementType, el.properties),
  }));

  const elements = staged.map(({ el, triggers, properties }): ExportedElement => {
    // A row some trigger points at carries its own global id as the reference
    // the rewritten entries name; otherwise any saved linkage id survives as-is.
    const referenceId = referenced.has(el.elementGlobalId)
      ? el.elementGlobalId
      : el.triggerFlowReferenceId;
    return {
      elementType: el.elementType,
      position: el.position,
      pageNumber: el.pageNumber,
      sectionNumber: el.sectionNumber,
      properties,
      uniqueIdToReplace: el.elementGlobalId,
      ...(triggers ? { triggers } : {}),
      ...(referenceId != null ? { triggerFlowReferenceId: referenceId } : {}),
    };
  });

  const file: FormExportJson = {
    title: detail.name,
    icon: detail.icon,
    description: detail.description,
    submitButtonColor: detail.submitButtonColor,
    elements,
  };
  return JSON.stringify(file, null, 2);
};

/** `Site inspection` → `site-inspection.midani.json` (the datasource template's slug
 *  idiom); a title that slugs to nothing (e.g. Arabic-only) falls back. */
export const formExportFileName = (title: string): string => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'form'}.midani.json`;
};

const isString = (v: unknown): v is string => typeof v === 'string';
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Parse + structurally validate an import file against the export contract
 *  above — the other half of the round-trip, kept in the same module so the
 *  two shapes can't drift apart. Throws on ANY mismatch (bad JSON included);
 *  the caller's catch owns the invalid-file toast. Unknown extra keys are
 *  dropped rather than passed through: the result is rebuilt field by field,
 *  so the POST body carries exactly the contract and nothing else. */
export const parseFormImportJson = (text: string): FormExportJson => {
  const raw = JSON.parse(text) as Record<string, unknown>;
  if (
    !raw ||
    typeof raw !== 'object' ||
    !isString(raw.title) ||
    !isString(raw.icon) ||
    !isString(raw.description) ||
    !isString(raw.submitButtonColor) ||
    !Array.isArray(raw.elements)
  ) {
    throw new Error('invalid form export file');
  }
  const elements = raw.elements.map((entry): ExportedElement => {
    const el = entry as Record<string, unknown>;
    if (
      !el ||
      typeof el !== 'object' ||
      !isString(el.elementType) ||
      !isString(el.properties) ||
      !isString(el.uniqueIdToReplace) ||
      !isFiniteNumber(el.position) ||
      !isFiniteNumber(el.pageNumber) ||
      !isFiniteNumber(el.sectionNumber)
    ) {
      throw new Error('invalid form export element');
    }
    return {
      elementType: el.elementType,
      position: el.position,
      pageNumber: el.pageNumber,
      sectionNumber: el.sectionNumber,
      properties: el.properties,
      uniqueIdToReplace: el.uniqueIdToReplace,
      ...(el.triggers != null ? { triggers: el.triggers as FormElementTriggers } : {}),
      ...(el.triggerFlowReferenceId != null
        ? { triggerFlowReferenceId: el.triggerFlowReferenceId as string }
        : {}),
    };
  });
  return {
    title: raw.title,
    icon: raw.icon,
    description: raw.description,
    submitButtonColor: raw.submitButtonColor,
    elements,
  };
};
